import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import BreadCrumbs from '@components/breadcumbs/BreadCumbs';
import BaseEnhancedTabs from '@components/tabs/BaseEnhancedTabs';
import { pushSuccess } from '@components/toast';
import { cameraController } from '@controllers/index';
import usePopUp from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { CustomTabPanel } from '@pages/overview/tabs/useTabsData';
import useCameraDetailProvider, { CameraDetailContext } from '../hook/CameraDetailsProvider';
import CameraInformation, { DetailsInformation } from './components/CameraInformation';
import ListPreset from './components/ListPreset';
import PopupCreateTask from './components/PopupCreateTask';
import RecordedEventLog from './components/cameraRecordedEventLog';
import GeoZonesTabs from './components/tabsGeozonesDetail';
import { PreviewImageWithGeoZone } from './components/tabsGeozonesDetail/solar/components/HistoriesPhoto';
import TabDetailedInformationOfMilesight from './components/detailedMileSight/TabDetailedInformationOfMilesight';
import { useQueryClient } from 'react-query';
import TaskManagementScreen from '@pages/task-management';
import useMedia from '@hooks/useMedia';
import { ContraventionProcessingJobType } from '@EcamModel/model';
import ReviewSessionListScreen from './components/tab-backdate-pcn';

export type Mode = 'create' | 'edit';

export enum TabIndex {
    BaseInfo,
    GeoZones,
    MoreInfo,
    EventLog,
}
export interface Action {
    create: boolean;
    edit: boolean;
}
export default function DetailsCamera() {
    const popupUpsertTask = usePopUp();
    const queryClient = useQueryClient();
    const geoZonesContext = useCameraDetailProvider();

    const { isMediumMD, isMobileSM } = useMedia();

    const {
        breadcrumbs,
        value,
        camera,
        setCamera,
        loading,
        setLoading,
        wakeUpLoading,
        setWakeUpLoading,
        openIMGPreview,
        setOpenIMGPreview,
        prevGeoZonesCameraSolar,
        notAvailable,
        tabs,
        scaleFullCameraPTZ,
        navigate,
        handleChangeTabs,
        handleAddExemption,
        handleDeleteExemption,
        handleUpdateCamera,
    } = geoZonesContext;

    const isDisplayBackDatePcn =
        (camera?.Zone?.IsReadyToGenPotentialContravention && camera?.IsVerificationsEnabled) ?? undefined;

    return (
        <CameraDetailContext.Provider value={geoZonesContext}>
            <Box>
                {loading ? (
                    <Stack direction="row" justifyContent="center" alignItems="center" minHeight={500}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <Box>
                        <Stack direction={isMediumMD ? 'column' : 'row'} justifyContent={'space-between'} spacing={isMediumMD ? 2 : 0}>
                            <Stack>
                                <Stack direction="row" alignItems="center" sx={{ mb: '8px' }} spacing={2}>
                                    <Typography variant="h3">{camera.Name}</Typography>
                                    {notAvailable && (
                                        <Tooltip
                                            arrow
                                            placement="top"
                                            title="The MAC address could not be found or is in the process of data synchronization."
                                        >
                                            <Chip
                                                size="small"
                                                sx={{
                                                    borderRadius: '16px',
                                                    height: 22,
                                                    fontSize: 12,
                                                    px: '5px',
                                                    cursor: 'pointer',
                                                }}
                                                label={'Not available'}
                                            />
                                        </Tooltip>
                                    )}
                                </Stack>
                                <BreadCrumbs breadcrumbs={breadcrumbs} />
                            </Stack>

                            <Stack direction={'row'} spacing={2}>
                                <Button
                                    variant="contained"
                                    // disabled={Boolean(camera.GeoZones?.length === 4) || !blobName}
                                    onClick={() => navigate('live-photos')}
                                >
                                    {isMobileSM ? 'Live photos' : 'Go to live photos'}
                                </Button>

                                {!camera.Deleted && camera.VendorCameraId !== null && (
                                    <LoadingButton
                                        loading={wakeUpLoading}
                                        loadingPosition="center"
                                        variant="orangeContained"
                                        disabled={wakeUpLoading}
                                        loadingIndicator="Waking..."
                                        onClick={() => {
                                            setWakeUpLoading(true);
                                            camera.Id && cameraController.wakeCameraByManually(camera.Id);
                                            setTimeout(() => {
                                                queryClient.invalidateQueries(['trailAuditEvent']);
                                                setWakeUpLoading(false);
                                                pushSuccess('Request sent successfully');
                                                queryClient.refetchQueries(['solar4gCamera', camera.Id?.toString()]);
                                            }, 10 * 1000);
                                        }}
                                    >
                                        Wake up
                                    </LoadingButton>
                                )}

                                <Button
                                    variant="outlinedGreen"
                                    onClick={() => {
                                        popupUpsertTask.setTrue();
                                    }}
                                >
                                    Create task
                                </Button>
                                {isMobileSM && <Button
                                    variant="contained"
                                    onClick={() => {
                                        navigate(`/cameras/${camera.Id}/edit`);
                                    }}
                                >
                                    Edit cam
                                </Button>}
                            </Stack>
                        </Stack>

                        <Box my={3}>
                            <BaseEnhancedTabs tabs={tabs} value={value} onChange={handleChangeTabs} minWidth={'10%'} />
                            {[
                                {
                                    name: 'Basic Information',
                                    children: (
                                        <CameraInformation
                                            camera={camera}
                                            setCamera={setCamera}
                                            loading={loading}
                                            setLoading={setLoading}
                                            onUpdateService={setCamera}
                                            onUpdateCamera={async (camera) => handleUpdateCamera(camera)}
                                            onAddExemption={handleAddExemption}
                                            onDeleteExemption={handleDeleteExemption}
                                            isHideAction={!!camera.Deleted}
                                        />
                                    ),
                                },
                                ...(camera.PTZCameraId
                                    ? [
                                          {
                                              name: 'List preset',
                                              children: <ListPreset />,
                                          },
                                      ]
                                    : []),
                                {
                                    name: 'Geo-zones',
                                    children: (
                                        <>
                                            <GeoZonesTabs />
                                            <PreviewImageWithGeoZone
                                                open={openIMGPreview}
                                                onClose={() => setOpenIMGPreview(false)}
                                                imgSrc={undefined}
                                                scaleX={scaleFullCameraPTZ}
                                                scaleY={scaleFullCameraPTZ}
                                                geoZones={camera.PTZCameraId !== null ? [] : prevGeoZonesCameraSolar}
                                                cameraType={
                                                    camera.PTZCameraId !== null ? CameraType.PTZ : CameraType.Solar
                                                }
                                            />
                                        </>
                                    ),
                                },
                                {
                                    name: 'More Information',
                                    children: <TabDetailedInformationOfMilesight />,
                                },
                                {
                                    name: 'Task',
                                    children: <TaskManagementScreen isShowTaskManagement />,
                                },
                                {
                                    name: 'Event logs',
                                    children: <RecordedEventLog />,
                                },
                                ...(isDisplayBackDatePcn !== undefined
                                    ? [
                                          {
                                              name: `${isDisplayBackDatePcn ? 'Backdate' : 'Potential'} PCN`,
                                              children: (
                                                  <ReviewSessionListScreen
                                                      type={
                                                          isDisplayBackDatePcn
                                                              ? ContraventionProcessingJobType.Backdate
                                                              : ContraventionProcessingJobType.Forecast
                                                      }
                                                  />
                                              ),
                                          },
                                      ]
                                    : []),
                            ].map((item, index) => {
                                return (
                                    <CustomTabPanel value={value} index={index}>
                                        {item.children}
                                    </CustomTabPanel>
                                );
                            })}
                        </Box>
                    </Box>
                )}
                {/* Popup */}
                <PopupCreateTask
                    {...popupUpsertTask}
                    isShowTaskManagement
                    cameraId={camera.Id!}
                    cameraName={camera.Name}
                    locationName={camera.Zone?.Location?.Name || ''}
                />
            </Box>
        </CameraDetailContext.Provider>
    );
}

interface GeoZonesDetailProps {
    detailGeoZones: DetailsInformation[];
}

export const BaseGeoZonesDetail: React.FC<GeoZonesDetailProps> = ({ detailGeoZones }) => {
    return (
        <Stack direction="row" spacing={1} alignItems="center" my={1}>
            {detailGeoZones.map((item, index) => (
                <Box key={item.id} display="flex" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontSize={14} color="#85858A">
                            {item.label}:
                        </Typography>
                        <Typography fontSize={14} color="#85858A">
                            {item.value}
                        </Typography>
                        {item.icon && <Box>{item.icon}</Box>}
                    </Stack>
                    {index < detailGeoZones.length - 1 && (
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                                borderRightWidth: 1,
                                borderColor: '#85858A',
                                px: '4px',
                            }}
                        />
                    )}
                </Box>
            ))}
        </Stack>
    );
};
